import { useState, useEffect } from "react";
import {
    Search, Mail, Phone, Clock, CheckCircle, AlertCircle,
    Loader2, Trash2, RefreshCw, Download, X
} from "lucide-react";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent } from "../../components/ui/card";
import { Checkbox } from "../../components/ui/checkbox";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { enquiriesService, type Enquiry } from "../../../lib/services/enquiriesService";
import type { Models } from "appwrite";

type EnquiryDoc = Enquiry & Models.Document;

// ── CSV export helper ─────────────────────────────────────────────────────────
function exportCSV(rows: EnquiryDoc[]) {
    const headers = ["Name", "Email", "Phone", "Service", "Message", "Date", "Status"];
    const escape = (v: string) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [
        headers.join(","),
        ...rows.map((e) =>
            [e.name, e.email, e.phone, e.service, e.message, e.date, e.status]
                .map(escape).join(",")
        ),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
}

export function AdminEnquiries() {
    const [enquiries, setEnquiries] = useState<EnquiryDoc[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [filter, setFilter] = useState("all");

    // Single delete
    const [deleteId, setDeleteId] = useState<string | null>(null);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);

    // Bulk selection + delete
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [isBulkDeleteOpen, setIsBulkDeleteOpen] = useState(false);
    const [bulkDeleting, setBulkDeleting] = useState(false);

    const load = () => {
        setLoading(true);
        setSelected(new Set());
        enquiriesService.list()
            .then(setEnquiries)
            .catch(console.error)
            .finally(() => setLoading(false));
    };

    useEffect(load, []);

    const toggleStatus = async (id: string, current: string) => {
        const next = current === "new" ? "replied" : "new";
        setEnquiries((prev) => prev.map((e) => e.$id === id ? { ...e, status: next } : e));
        try { await enquiriesService.updateStatus(id, next as "new" | "replied"); }
        catch { load(); }
    };

    const confirmDelete = (id: string) => { setDeleteId(id); setIsDeleteOpen(true); };

    const handleDelete = async () => {
        if (!deleteId) return;
        setIsDeleteOpen(false);
        setEnquiries((prev) => prev.filter((e) => e.$id !== deleteId));
        try { await enquiriesService.remove(deleteId); setDeleteId(null); }
        catch { load(); }
    };

    const handleBulkDelete = async () => {
        setBulkDeleting(true);
        const ids = [...selected];
        setEnquiries((prev) => prev.filter((e) => !selected.has(e.$id)));
        setSelected(new Set());
        setIsBulkDeleteOpen(false);
        try {
            await Promise.all(ids.map((id) => enquiriesService.remove(id)));
        } catch { load(); }
        finally { setBulkDeleting(false); }
    };

    // Derived data — must be declared before selection helpers that use it
    const filtered = enquiries.filter((e) => {
        const q = search.toLowerCase();
        const matchSearch =
            e.name.toLowerCase().includes(q) ||
            e.email.toLowerCase().includes(q) ||
            e.service.toLowerCase().includes(q);
        const matchFilter = filter === "all" || e.status === filter;
        return matchSearch && matchFilter;
    });

    const newCount = enquiries.filter((e) => e.status === "new").length;
    const repliedCount = enquiries.filter((e) => e.status === "replied").length;

    // Selection helpers
    const toggleOne = (id: string) => {
        setSelected((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };
    const toggleAll = () => {
        if (selected.size === filtered.length) {
            setSelected(new Set());
        } else {
            setSelected(new Set(filtered.map((e) => e.$id)));
        }
    };
    const allSelected = filtered.length > 0 && selected.size === filtered.length;
    const someSelected = selected.size > 0 && !allSelected;

    const StatusBadge = ({ e }: { e: EnquiryDoc }) => (
        <Badge
            variant="outline"
            className={`cursor-pointer transition-colors ${e.status === "new"
                ? "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
                : "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                }`}
            onClick={() => toggleStatus(e.$id, e.status)}
        >
            {e.status === "new" ? (
                <><AlertCircle className="w-3.5 h-3.5 mr-1.5" />New</>
            ) : (
                <><CheckCircle className="w-3.5 h-3.5 mr-1.5" />Replied</>
            )}
        </Badge>
    );

    return (
        <div className="space-y-4">
            {/* ── Single consolidated header row ── */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pb-3 border-b border-border">
                {/* Left: title + count + tabs */}
                <div className="flex items-center gap-3 shrink-0">
                    <h2 className="text-base font-semibold text-foreground">Enquiries</h2>
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{enquiries.length}</span>
                </div>

                {/* Underline tabs */}
                <div className="flex items-center gap-0 border-b border-transparent -mb-3">
                    {(["all", "new", "replied"] as const).map((f) => (
                        <button
                            key={f}
                            onClick={() => setFilter(f)}
                            className={`capitalize text-sm px-3 pb-3 font-medium transition-colors border-b-2 ${filter === f
                                ? "border-primary text-foreground"
                                : "border-transparent text-muted-foreground hover:text-white"
                                }`}
                        >
                            {f}
                            {f === "new" && newCount > 0 && (
                                <span className="ml-1.5 text-[10px] font-semibold text-primary">{newCount}</span>
                            )}
                            {f === "replied" && repliedCount > 0 && (
                                <span className="ml-1.5 text-[10px] font-semibold text-muted-foreground">{repliedCount}</span>
                            )}
                        </button>
                    ))}
                </div>

                {/* Right: search + actions */}
                <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto sm:ml-auto mt-2 sm:mt-0">
                    {/* Search */}
                    <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                        <Input
                            placeholder="Search…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-8 h-8 w-44 text-sm bg-background border border-border shadow-none focus-visible:ring-1"
                        />
                        {search && (
                            <button onClick={() => setSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white">
                                <X className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </div>

                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => exportCSV(filtered)}
                        disabled={filtered.length === 0}
                        className="h-8 gap-1.5 text-muted-foreground hover:text-white shadow-none"
                    >
                        <Download className="w-3.5 h-3.5" />
                        <span className="hidden sm:inline">Export CSV</span>
                    </Button>

                    {selected.size > 0 && (
                        <Button variant="destructive" size="sm" onClick={() => setIsBulkDeleteOpen(true)} className="h-8 gap-1.5">
                            <Trash2 className="w-3.5 h-3.5" />
                            Delete {selected.size}
                        </Button>
                    )}

                    <Button variant="ghost" size="sm" onClick={load} disabled={loading} className="h-8 text-muted-foreground hover:text-white gap-1.5">
                        <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
                    </Button>
                </div>
            </div>

            {/* ── Selection banner ── */}
            {selected.size > 0 && (
                <div className="flex items-center gap-3 px-3 py-2 bg-primary/5 border border-primary/20 rounded text-sm text-primary font-medium">
                    <span>{selected.size} of {filtered.length} selected</span>
                    <button onClick={() => setSelected(new Set())} className="ml-auto text-primary/70 hover:text-primary">
                        <X className="w-3.5 h-3.5" />
                    </button>
                </div>
            )}

            {/* ── Content ── */}
            {loading ? (
                <div className="flex flex-col items-center justify-center py-20 bg-card rounded border border-border min-h-[40vh]">
                    <Loader2 className="w-8 h-8 animate-spin text-primary mb-4" />
                    <p className="text-sm font-medium text-muted-foreground">Loading enquiries...</p>
                </div>
            ) : filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-24 bg-card rounded border border-border text-center min-h-[40vh]">
                    <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
                        <Mail className="w-6 h-6 text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground">No enquiries found</h3>
                    <p className="text-sm text-muted-foreground mt-1 max-w-sm">No requests match your current filters or search terms.</p>
                </div>
            ) : (
                <>
                    {/* Mobile cards */}
                    <div className="md:hidden space-y-3">
                        {filtered.map((e: EnquiryDoc) => (
                            <Card key={e.$id} className={`overflow-hidden shadow-none transition-colors ${selected.has(e.$id) ? "border-primary/40 bg-primary/5" : ""}`}>
                                <CardContent className="p-4">
                                    <div className="flex items-start gap-3 mb-3">
                                        <Checkbox
                                            checked={selected.has(e.$id)}
                                            onCheckedChange={() => toggleOne(e.$id)}
                                            className="mt-1 shrink-0"
                                        />
                                        <div className="flex items-center gap-3 flex-1 min-w-0">
                                            <div className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-sm shrink-0 uppercase">
                                                {e.name.charAt(0)}
                                            </div>
                                            <div className="min-w-0">
                                                <p className="font-semibold text-foreground truncate">{e.name}</p>
                                                <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                                                    <Clock className="w-3 h-3" /> {e.date}
                                                </p>
                                            </div>
                                        </div>
                                        <StatusBadge e={e} />
                                    </div>
                                    <div className="space-y-2 pl-7">
                                        <a href={`mailto:${e.email}`} className="text-sm text-primary hover:underline flex items-center gap-2">
                                            <Mail className="w-3.5 h-3.5 text-muted-foreground" /> {e.email}
                                        </a>
                                        <a href={`tel:${e.phone}`} className="text-sm text-muted-foreground flex items-center gap-2">
                                            <Phone className="w-3.5 h-3.5" /> {e.phone}
                                        </a>
                                        <Badge variant="secondary" className="text-xs">{e.service}</Badge>
                                        <p className="text-sm text-muted-foreground bg-muted/40 rounded p-2.5 leading-relaxed">{e.message}</p>
                                        <div className="flex justify-end">
                                            <Button variant="ghost" size="sm" onClick={() => confirmDelete(e.$id)} className="text-destructive hover:bg-destructive/10 h-7 text-xs">
                                                <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
                                            </Button>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>

                    {/* Desktop table */}
                    <div className="hidden md:block border border-border rounded overflow-hidden">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-muted/50 hover:bg-muted/50">
                                    <TableHead className="w-10 pl-4">
                                        <Checkbox
                                            checked={allSelected}
                                            ref={(el) => { if (el) (el as any).indeterminate = someSelected; }}
                                            onCheckedChange={toggleAll}
                                        />
                                    </TableHead>
                                    <TableHead className="w-[220px] font-semibold">Client</TableHead>
                                    <TableHead className="w-[190px] font-semibold">Contact</TableHead>
                                    <TableHead className="w-[160px] font-semibold">Interest</TableHead>
                                    <TableHead className="font-semibold">Message</TableHead>
                                    <TableHead className="w-[110px] font-semibold">Date</TableHead>
                                    <TableHead className="w-[120px] font-semibold">Status</TableHead>
                                    <TableHead className="w-[60px]" />
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filtered.map((e) => (
                                    <TableRow
                                        key={e.$id}
                                        className={`group ${selected.has(e.$id) ? "bg-primary/5 hover:bg-primary/8" : ""}`}
                                    >
                                        <TableCell className="pl-4">
                                            <Checkbox
                                                checked={selected.has(e.$id)}
                                                onCheckedChange={() => toggleOne(e.$id)}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-xs shrink-0 uppercase">
                                                    {e.name.charAt(0)}
                                                </div>
                                                <span className="font-medium text-foreground text-sm">{e.name}</span>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex flex-col gap-1">
                                                <a href={`mailto:${e.email}`} className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1.5 transition-colors">
                                                    <Mail className="w-3 h-3" />
                                                    <span className="truncate max-w-[140px]">{e.email}</span>
                                                </a>
                                                <a href={`tel:${e.phone}`} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1.5 transition-colors">
                                                    <Phone className="w-3 h-3" /> {e.phone}
                                                </a>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="secondary" className="font-normal text-xs">{e.service}</Badge>
                                        </TableCell>
                                        <TableCell>
                                            <p className="text-xs text-muted-foreground group-hover:text-foreground line-clamp-2 leading-relaxed transition-colors" title={e.message}>
                                                {e.message}
                                            </p>
                                        </TableCell>
                                        <TableCell>
                                            <span className="text-xs text-muted-foreground whitespace-nowrap">{e.date}</span>
                                        </TableCell>
                                        <TableCell>
                                            <StatusBadge e={e} />
                                        </TableCell>
                                        <TableCell className="text-right pr-3">
                                            <Button
                                                variant="ghost" size="icon"
                                                onClick={() => confirmDelete(e.$id)}
                                                className="h-7 w-7 text-muted-foreground hover:bg-destructive/10 hover:text-destructive opacity-0 group-hover:opacity-100 transition-all"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </>
            )}

            {/* ── Single Delete Dialog ── */}
            <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this enquiry?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This action cannot be undone. This will permanently remove the enquiry from your database.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* ── Bulk Delete Dialog ── */}
            <AlertDialog open={isBulkDeleteOpen} onOpenChange={setIsBulkDeleteOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete {selected.size} {selected.size === 1 ? "enquiry" : "enquiries"}?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will permanently delete <strong>{selected.size}</strong> selected {selected.size === 1 ? "enquiry" : "enquiries"}. This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={bulkDeleting}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleBulkDelete}
                            disabled={bulkDeleting}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90 gap-2"
                        >
                            {bulkDeleting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                            Delete {selected.size}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
